<template>
  <div class="slider-select">
    <slot />
    <swiper
      class="slider-select-main"
      v-bind="SWIPER_MAIN_SLIDER_OPTIONS as any"
      @swiper="setMainInstance"
      @slide-change="setModelValueBySlideIndex($event.activeIndex)"
    >
      <swiper-slide
        v-for="(option, index) in options"
        :key="index"
        class="slider-select-main-item hover:cursor-pointer"
        @click="openViewer(index)"
      >
        <nuxt-img
          :src="option.imageSrc"
          :alt="option?.label || `slider image number ${index}`"
          loading="lazy"
          sizes="xs:100vw md:100vw lg:375px"
        ></nuxt-img>
      </swiper-slide>
    </swiper>
    <lazy-swiper
      v-if="!withoutThumbnail"
      class="slider-select-cover"
      v-bind="SWIPER_COVER_SLIDER_OPTIONS as any"
      @swiper="setCoverInstance"
    >
      <sf-button
        variant="tertiary"
        aria-label="prev button"
        :disabled="!!isBeginning"
        class="slider-select-navigation-prev"
        @click="setActiveSlides(modelValueIndex - 1)"
      >
        <icons-icon-chevron-left size="base" class="icon" />
      </sf-button>
      <lazy-swiper-slide
        v-for="(option, index) in options"
        :key="index"
        :class="{ 'slider-select-cover__active': index === modelValueIndex }"
        class="slider-select-thumbnail-item"
        @click="setModelValue(option.value)"
      >
        <nuxt-img
          :src="option.imageSmallSrc || option.imageSrc"
          :alt="option?.label || `slider image number ${index}`"
          draggable="false"
          loading="lazy"
          sizes="xs:90px md:180px"
        ></nuxt-img>
      </lazy-swiper-slide>
      <sf-button
        variant="tertiary"
        aria-label="next button"
        :disabled="!!isEnd"
        class="slider-select-navigation-next"
        @click="setActiveSlides(modelValueIndex + 1)"
      >
        <icons-icon-chevron-right size="base" class="icon" />
      </sf-button>
    </lazy-swiper>
    <div v-if="!withoutSelectedLabel" class="slider-select-selected-label">
      {{ options[modelValueIndex]?.label }}
    </div>
  </div>
</template>
<script lang="ts" setup>
import { isEqual } from "lodash-es";
import { useDebounceFn } from "@vueuse/shared";
import type { SliderSelectCtrl, SliderSelectOption } from "./types";
import {
  SLIDER_SELECT_INJECT_TOKEN,
  SWIPER_COVER_SLIDER_OPTIONS,
  SWIPER_MAIN_SLIDER_OPTIONS,
} from "./variables";

const props = defineProps<{
  modelValue: unknown;
  withoutThumbnail?: boolean;
  withoutSelectedLabel?: boolean;
}>();

const emit = defineEmits<{
  (_event: "update:modelValue", _param: unknown): void;
}>();

const {
  swiper: swiperInstanceMain,
  setInstance: setMainInstance,
  changeSlide: changeSlideMain,
  isBeginning,
  isEnd,
} = useSwiperInstance();
const { setInstance: setCoverInstance, changeSlide: changeSlideCover } =
  useSwiperInstance();
const { openImageViewer } = useImageViewer();

const instance = getCurrentInstance();
const options = ref<SliderSelectOption[]>([]);
const modelValueIndex = computed<number>(() => {
  const index = options.value.findIndex((opt) =>
    isEqual(opt.value, props.modelValue),
  );
  return index === -1 ? 0 : index;
});

const updateOptionsDebounce = useDebounceFn(() => {
  options.value = options.value.slice();
}, 50);

function registerOption(opt: SliderSelectOption): void {
  options.value.push(opt);
  updateOptions();
}

function unregisterOption(opt: SliderSelectOption): void {
  const index = options.value.findIndex((optExisted) =>
    isEqual(opt, optExisted),
  );
  if (index !== -1) {
    options.value.splice(index, 1);
    updateOptions();
  }
}

function updateOptions(): void {
  if (process.client) {
    updateOptionsDebounce();
  } else {
    options.value = options.value.slice();
  }
}

function setActiveSlides(index: number): void {
  changeSlideMain(index);
  changeSlideCover(index);
}

function setModelValueBySlideIndex(index: number): void {
  const opt = options.value[index];
  if (opt) {
    setModelValue(opt.value);
  }
}

function setModelValue(value: unknown): void {
  emit("update:modelValue", value);
}

watch(modelValueIndex, (newValue) => {
  setActiveSlides(newValue);
});

watch(swiperInstanceMain, (newValue) => {
  if (newValue) {
    setActiveSlides(modelValueIndex.value);
  }
});

function openViewer(initialIndex: number): void {
  const preparedImages = options.value.map((i) => ({
    largeUrl: i.imageSrc,
    alt: i.label || "",
  }));

  openImageViewer(initialIndex, preparedImages);
}

defineExpose({ registerOption, unregisterOption } as SliderSelectCtrl);
provide(SLIDER_SELECT_INJECT_TOKEN, instance?.exposed);
</script>
<style lang="scss">
@import "@/node_modules/swiper/modules/pagination";

.slider-select {
  .swiper-slide {
    @apply select-none;

    img {
      @apply h-full w-full;
    }
  }

  .swiper-pagination {
    --swiper-pagination-bullet-border-radius: 0;
    --swiper-pagination-color: #477cbf;
  }

  &-cover {
    $self: &;

    @apply mt-1;

    .swiper-slide {
      @apply mr-1 h-[88px] w-[88px] cursor-pointer border-2 border-transparent last:mr-0;

      img {
        @apply object-cover;
      }

      &.slider-select-cover__active {
        @apply border-sky-600;
      }
    }
  }

  &-wrapper {
    @apply relative;
  }

  &-navigation {
    &-prev,
    &-next {
      @apply absolute top-[50%] z-[1] h-6 w-6 -translate-y-[50%] bg-white/50 p-0 text-neutral-500 hover:bg-white/70 active:bg-white/80;
    }

    &-prev {
      @apply left-1;
    }

    &-next {
      @apply right-1;
    }
  }

  &-selected-label {
    @apply pt-1 text-center text-zinc-500;
  }
}
</style>
